import {doNothing} from '@raison/toolbox';
import {RsnDialog, RsnPicker} from '@raison/ui-kit';
import {format} from 'date-fns/format';
import {getMonth} from 'date-fns/getMonth';
import {subMonths} from 'date-fns/subMonths';
import {subWeeks} from 'date-fns/subWeeks';
import React, {useMemo} from 'react';
import {TransactionsHistory} from '~/entities/transactions-history';
import {TransactionIcons} from '~/features/transitions-list';

const {maximum, month, three_month, today, week} = TransactionIcons;

const apiFormat = (date: Date) => {
    return format(date, 'yyyy-MM-dd');
};

const uiFormat = (date: Date) => {
    return format(date, 'MMM dd');
};

const formatDayNumber = (date: Date) => Intl.DateTimeFormat('en-US', {day: '2-digit'}).format(date);

export const DateFilterPopup = () => {
    const [{date: appliedFilter}, {apply: applyFilters}] = TransactionsHistory.useFilters();

    type rangeType = 'all' | 'today' | 'week' | 'month' | 'three_month';
    const todayDate = new Date();
    const lastWeekDate = subWeeks(todayDate, 1);
    const lastMonthDate = subMonths(todayDate, 1);
    const threeMonthDate = subMonths(todayDate, 3);
    const prevMonthDate = subMonths(todayDate, 1);
    const monthName = format(todayDate, 'MMM'); // February -> Feb

    const monthsAreEqual = getMonth(lastWeekDate) === getMonth(todayDate);

    const weekRange = `${uiFormat(lastWeekDate)} – ${monthsAreEqual ? formatDayNumber(todayDate) : `${formatDayNumber(prevMonthDate)} ${monthName}`}`;
    const monthRange = `${uiFormat(lastMonthDate)} – ${uiFormat(todayDate)}`;
    const threeMonthRange = `${uiFormat(threeMonthDate)} – ${uiFormat(todayDate)}`;

    const ranges: {[key in rangeType]: string} = {
        all: '',
        today: apiFormat(todayDate),
        week: apiFormat(lastWeekDate),
        month: apiFormat(lastMonthDate),
        three_month: apiFormat(threeMonthDate),
    };

    const find = Object.entries(ranges).find(item => `${item[1]}_${apiFormat(todayDate)}` === appliedFilter);

    const type = find ? find[0] : 'all';

    const selected = {type, value: appliedFilter || ''};
    const {onClose: close = doNothing} = RsnDialog.useControls();

    const options: RsnPicker.OptionObject<rangeType>[] = useMemo(
        () => [
            {
                value: 'all',
                icon: {src: maximum},
                title: 'Maximum',
                hint: 'All time',
            },
            {
                value: 'today',
                icon: {src: today},
                title: 'Today',
                hint: uiFormat(todayDate),
            },
            {
                value: 'week',
                icon: {src: week},
                title: 'Last 7 days',
                hint: weekRange,
            },
            {
                value: 'month',
                icon: {src: month},
                title: 'Last 30 days',
                hint: monthRange,
            },
            {
                value: 'three_month',
                icon: {src: three_month},
                title: 'Last 90 days',
                hint: threeMonthRange,
            },
        ],
        [todayDate, weekRange, monthRange, threeMonthRange]
    );

    const handleCheck = (range: rangeType) => {
        let from = '';
        const to = apiFormat(todayDate);

        switch (range) {
            case 'today':
                from = apiFormat(todayDate);
                break;
            case 'week':
                from = apiFormat(lastWeekDate);
                break;
            case 'month':
                from = apiFormat(lastMonthDate);
                break;
            case 'three_month':
                from = apiFormat(threeMonthDate);
                break;
            default:
                from = '';
                break;
        }

        applyFilters({date: range === 'all' ? '' : `${from}_${to}`});
        close();
    };

    return (
        <RsnPicker.Dialog
            title={'Operation date'}
            value={selected.type as rangeType}
            options={options}
            onChange={handleCheck}
        />
    );
};
